import React from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import StarIcon from '@material-ui/icons/Star';
import FavoriteIcon from '@material-ui/icons/Favorite';
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder';
import RoomIcon from '@material-ui/icons/Room';
import PhoneIcon from '@material-ui/icons/Phone';
import LanguageIcon from '@material-ui/icons/Language';
import { Link, useParams } from 'react-router-dom';
import EmptyState from '../components/common/EmptyState';
import { useTravelGuide } from '../context/TravelGuideContext';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(3),
  },
  banner: {
    borderRadius: 26,
    border: '1px solid rgba(166, 140, 118, 0.28)',
    background: 'linear-gradient(118deg, #fff8ef 0%, #f3e7d9 60%, #ebdac9 130%)',
    boxShadow: '0 16px 32px rgba(63, 46, 31, 0.1)',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  heading: {
    fontFamily: '"Manrope", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
    color: '#2f261f',
  },
  subheading: {
    color: '#645649',
    marginTop: theme.spacing(0.8),
    maxWidth: 760,
  },
  actionRow: {
    marginTop: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  mediaPanel: {
    borderRadius: 20,
    border: '1px solid rgba(169, 144, 124, 0.28)',
    background: 'linear-gradient(180deg, #fffdf9 0%, #f9f1e7 100%)',
    overflow: 'hidden',
  },
  heroImage: {
    width: '100%',
    height: 460,
    objectFit: 'cover',
    display: 'block',
    [theme.breakpoints.down('sm')]: {
      height: 280,
    },
  },
  infoPanel: {
    borderRadius: 20,
    border: '1px solid rgba(169, 144, 124, 0.28)',
    background: 'linear-gradient(180deg, #fffefb 0%, #f8efe3 100%)',
    padding: theme.spacing(2),
    height: '100%',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.2),
  },
  statCard: {
    borderRadius: 14,
    border: '1px solid rgba(169, 144, 124, 0.24)',
    background: '#fffaf3',
    padding: theme.spacing(1.2),
  },
  statLabel: {
    color: '#7b6b5d',
    fontSize: '0.78rem',
    fontWeight: 700,
  },
  statValue: {
    fontFamily: '"Manrope", sans-serif',
    fontWeight: 800,
    fontSize: '1.05rem',
    color: '#3b3027',
    marginTop: 2,
  },
  metaRow: {
    marginTop: theme.spacing(1.6),
    display: 'grid',
    gap: theme.spacing(0.9),
  },
  aboutBlock: {
    marginTop: theme.spacing(1.6),
    borderRadius: 14,
    border: '1px solid rgba(169, 144, 124, 0.24)',
    background: '#fffaf3',
    padding: theme.spacing(1.2),
  },
  aboutTitle: {
    fontWeight: 800,
    color: '#3b3027',
    marginBottom: theme.spacing(0.6),
  },
  metaItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    color: '#4c4137',
  },
  chips: {
    marginTop: theme.spacing(1.5),
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  cuisineChip: {
    background: 'rgba(245, 233, 217, 0.9)',
    border: '1px solid rgba(169, 144, 124, 0.28)',
    color: '#4d4033',
    fontWeight: 700,
  },
  bottomActions: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
}));

const PlaceDetailsPage = () => {
  const classes = useStyles();
  const { placeId } = useParams();
  const { state, dispatch } = useTravelGuide();
  const place = state.placeCatalog[placeId];

  if (!place) {
    return (
      <Container maxWidth="md" style={{ marginTop: 24 }}>
        <EmptyState
          title="Place not found"
          description="This place is not in your current session data. Explore again and open details from results."
          actionLabel="Go to Explore"
          actionTo="/explore"
        />
      </Container>
    );
  }

  const isSaved = state.savedPlaces.includes(place.id);

  return (
    <Container maxWidth="xl" className={classes.root}>
      <Paper elevation={0} className={classes.banner}>
        <Typography variant="h4" className={classes.heading}>{place.name}</Typography>
        <Typography variant="body1" className={classes.subheading}>
          {place.address || 'Address unavailable'} {place.ranking ? `• ${place.ranking}` : ''}
        </Typography>
        <Box className={classes.actionRow}>
          <Button component={Link} to="/explore" startIcon={<ArrowBackIcon />} variant="outlined" color="primary">
            Back to Explore
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={isSaved ? <FavoriteIcon /> : <FavoriteBorderIcon />}
            onClick={() => dispatch({ type: 'TOGGLE_SAVE', payload: place })}
          >
            {isSaved ? 'Saved' : 'Save Place'}
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper elevation={0} className={classes.mediaPanel}>
            <img src={place.photoUrl} alt={place.name} className={classes.heroImage} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper elevation={0} className={classes.infoPanel}>
            <Typography variant="h5" style={{ fontFamily: '"Manrope", sans-serif', fontWeight: 800 }}>
              Place summary
            </Typography>

            <div className={classes.statsGrid}>
              <Paper elevation={0} className={classes.statCard}>
                <Typography className={classes.statLabel}>Rating</Typography>
                <Typography className={classes.statValue}>
                  <StarIcon style={{ fontSize: 16, color: '#f59e0b', verticalAlign: 'text-bottom', marginRight: 4 }} />
                  {place.rating.toFixed(1)}
                </Typography>
              </Paper>
              <Paper elevation={0} className={classes.statCard}>
                <Typography className={classes.statLabel}>Reviews</Typography>
                <Typography className={classes.statValue}>{place.reviews}</Typography>
              </Paper>
              <Paper elevation={0} className={classes.statCard}>
                <Typography className={classes.statLabel}>Price Level</Typography>
                <Typography className={classes.statValue}>{place.priceLevel || 'N/A'}</Typography>
              </Paper>
              <Paper elevation={0} className={classes.statCard}>
                <Typography className={classes.statLabel}>Ranking</Typography>
                <Typography className={classes.statValue}>{place.ranking || 'Not ranked'}</Typography>
              </Paper>
            </div>

            <div className={classes.metaRow}>
              {place.address && (
                <div className={classes.metaItem}>
                  <RoomIcon fontSize="small" color="secondary" />
                  <Typography variant="body2">{place.address}</Typography>
                </div>
              )}
              {place.phone && (
                <div className={classes.metaItem}>
                  <PhoneIcon fontSize="small" color="secondary" />
                  <Typography variant="body2">{place.phone}</Typography>
                </div>
              )}
              {place.distanceText && (
                <div className={classes.metaItem}>
                  <RoomIcon fontSize="small" color="secondary" />
                  <Typography variant="body2">Distance: {place.distanceText}</Typography>
                </div>
              )}
            </div>

            {place.cuisines?.length > 0 && (
              <div className={classes.chips}>
                {place.cuisines.map((item) => (
                  <Chip key={item} label={item} size="small" className={classes.cuisineChip} />
                ))}
              </div>
            )}

            {(place.summaryText || place.statusText || place.category) && (
              <div className={classes.aboutBlock}>
                <Typography variant="subtitle2" className={classes.aboutTitle}>About this place</Typography>
                {place.summaryText && (
                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: 6 }}>
                    {place.summaryText}
                  </Typography>
                )}
                {place.statusText && (
                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>
                    Status: {place.statusText}
                  </Typography>
                )}
                {place.category && (
                  <Typography variant="body2" color="textSecondary">
                    Category: {place.category}
                  </Typography>
                )}
              </div>
            )}

            <div className={classes.bottomActions}>
              {place.sourceUrl && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => window.open(place.sourceUrl, '_blank', 'noopener,noreferrer')}
                >
                  TripAdvisor
                </Button>
              )}
              {place.website && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<LanguageIcon />}
                  onClick={() => window.open(place.website, '_blank', 'noopener,noreferrer')}
                >
                  Website
                </Button>
              )}
            </div>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PlaceDetailsPage;
